import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, Loader2, Send, MessageCircle } from 'lucide-react';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';

const fmt = (ts) => new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const Bubble = ({ m }) => {
  const mine = m.direction === 'inbound';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`hc-message-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? 'bg-[#922A8C] text-white rounded-br-sm' : 'bg-[#F6EAF5] text-[#363636] rounded-bl-sm'}`}>
        {!mine && m.sender_name && <div className="text-xs font-semibold text-[#4c124a] mb-0.5">{m.sender_name}</div>}
        <p className="whitespace-pre-wrap text-[15px]">{m.message}</p>
        <div className={`text-[11px] mt-1 ${mine ? 'text-white/70 text-right' : 'text-gray-500'}`}>{fmt(m.timestamp)}</div>
      </div>
    </div>
  );
};

const ClosedNote = ({ window }) => (
  <div className="flex gap-3 rounded-2xl bg-[#F6EAF5] p-4 text-[#4c124a]" data-testid="hc-messaging-closed">
    <Clock className="w-5 h-5 shrink-0 mt-0.5" />
    <p className="text-sm">
      Thanks for reaching out! Messaging is available <b>{window.hours_text}</b>. You can send your next message <b>{window.next_open_text}</b>.
    </p>
  </div>
);

export default function HcMessagesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => hcApi.get('/messages').then(({ data }) => setData(data)).catch((e) => setError(hcError(e))), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setError('');
    try {
      const { data: res } = await hcApi.post('/messages', { message: text });
      setData((d) => ({ ...d, messages: [...d.messages, res.message] }));
      setText('');
    } catch (err) {
      setError(hcError(err));
      load();
    } finally {
      setSending(false);
    }
  };

  const open = data?.window?.open;
  return (
    <HcLayout>
      <div className="max-w-4xl hc-fade-up" data-testid="hc-messages-page">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]">Messages</h1>
        <p className="text-gray-500 mt-2">{data?.case_manager ? `Message your case manager, ${data.case_manager.name}.` : 'Message your case manager.'}</p>
        {!data && !error && <Loader2 className="w-8 h-8 mt-8 animate-spin text-[#922A8C]" />}
        {data && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col h-[65vh]">
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="hc-message-thread">
              {data.messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 text-center">
                  <MessageCircle className="w-12 h-12 mb-2" />
                  <p>No messages yet. Say hello to your case manager!</p>
                </div>
              )}
              {data.messages.map((m) => <Bubble key={m.id} m={m} />)}
              <div ref={endRef} />
            </div>
            <div className="border-t border-gray-100 p-4 space-y-3">
              {!data.case_manager && <p className="text-sm text-gray-500" data-testid="hc-no-case-manager">You don't have a case manager assigned yet.</p>}
              {data.case_manager && !open && <ClosedNote window={data.window} />}
              {error && <p className="text-sm text-red-600" role="alert" data-testid="hc-message-error">{error}</p>}
              <form onSubmit={send} className="flex gap-3">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000}
                  disabled={!open || !data.case_manager || sending} placeholder={open ? 'Type your message...' : 'Messaging is closed right now'}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
                  className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40 disabled:bg-gray-50"
                  data-testid="hc-message-input" />
                <button type="submit" disabled={!open || !data.case_manager || sending || !text.trim()} data-testid="hc-message-send"
                  className="self-end h-12 px-5 rounded-xl bg-[#922A8C] text-white font-semibold flex items-center gap-2 hover:bg-[#7a2275] disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </HcLayout>
  );
}
